import { Component, OnInit, inject, signal, computed, effect } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { DatePipe } from '@angular/common';
import { Todo } from 'shared-types';
import { TodoService } from '../../core/services/todo.service';
import { WalletService } from '../../core/services/wallet.service';

@Component({
  selector: 'app-todo',
  standalone: true,
  imports: [FormsModule, DatePipe],
  styleUrls: ['./todo.component.scss'],
  template: `
    <div class="win95-todo__toolbar win95-todo">
      <div class="win95-todo__tabs">
        <button 
          class="win95-todo__tab" 
          [class.win95-todo__tab--active]="activeTab() === 'global'"
          (click)="activeTab.set('global')"
        >
          Global
        </button>
        <button 
          class="win95-todo__tab" 
          [class.win95-todo__tab--active]="activeTab() === 'personal'"
          (click)="activeTab.set('personal')"
          [disabled]="!isWalletConnected()"
        >
          Personal
        </button>
      </div>
      <button class="win95-todo__btn" (click)="refreshTodos()">Refresh</button>
      <span class="win95-todo__sep"></span>
      @if (loadingTodos) {
      <span class="win95-todo__status win95-todo__status--loading">Loading…</span>
      } @if (!loadingTodos && !errorMsg && displayedTodos().length === 0) {
      <span class="win95-todo__status">
        @if (!isWalletConnected() && activeTab() === 'personal') {
          Connect wallet to see personal todos.
        } @else {
          No todos available.
        }
      </span>
      } @if (errorMsg) {
      <span class="win95-todo__status win95-todo__status--error">{{ errorMsg }}</span>
      }
    </div>

    <div class="win95-todo__body win95-todo">
      @if (isWalletConnected() || activeTab() === 'global') {
      <div class="win95-todo__input-row">
        <input
          class="win95-todo__input"
          type="text"
          placeholder="New todo…"
          [(ngModel)]="newTitle"
          (keydown.enter)="addTodo()"
        />
        <button class="win95-todo__btn win95-todo__btn--primary" (click)="addTodo()">
          Add {{ activeTab() === 'global' ? 'Global' : 'Personal' }}
        </button>
      </div>
      }

      <div class="win95-todo__list">
        <div class="win95-todo__list-header" [class.with-creator]="activeTab() === 'global'">
          <div class="win95-todo__col-check"></div>
          <div class="win95-todo__col-title">Title</div>
          @if (activeTab() === 'global') {
          <div class="col-creator">Creator</div>
          }
          <div class="col-created">Created</div>
          <div class="col-actions"></div>
        </div>

        <div class="win95-todo__list-body">
          @for (t of displayedTodos(); track t?.id) {
          <div class="win95-todo__row" [class.with-creator]="activeTab() === 'global'">
            <div class="win95-todo__col-check">
              <input 
                type="checkbox" 
                [checked]="t.done" 
                (change)="toggleTodo(t)"
                [disabled]="!canToggleTodo(t)"
              />
            </div>
            <div class="win95-todo__col-title" [class.win95-todo__col-title--done]="t.done">
              {{ t.title }}
            </div>
            @if (activeTab() === 'global') {
            <div class="col-creator" [title]="t.creator">
              {{ t.creator }}
            </div>
            }
            <div class="col-created">
              {{ (t.createdAt | date : 'dd.MM.yyyy HH:mm') || t.createdAt }}
            </div>
            <div class="col-actions">
              @if (canDeleteTodo(t)) {
              <button
                class="win95-todo__btn win95-todo__btn--title"
                title="Delete"
                (click)="deleteTodo(t)"
              >
                <img src="assets/img/trash.svg" alt="Delete" width="12" height="12" />
              </button>
              }
            </div>
          </div>
          }
        </div>
      </div>
    </div>
  `,
})
export class TodoComponent implements OnInit {
  private readonly todoService = inject(TodoService);
  private readonly walletService = inject(WalletService);

  globalTodos = signal<Todo[]>([]);
  personalTodos = signal<Todo[]>([]);
  activeTab = signal<'global' | 'personal'>('global');
  newTitle = '';
  loadingTodos = false;
  errorMsg: string | null = null;

  displayedTodos = computed(() => {
    return this.activeTab() === 'global' ? this.globalTodos() : this.personalTodos();
  });

  walletConnection = this.walletService.walletConnection;
  
  isWalletConnected = computed(() => !!this.walletConnection());

  ngOnInit() {
    this.refreshTodos();
    
    // Watch for tab changes and refresh todos
    effect(() => {
      this.activeTab(); // Track the signal
      this.refreshTodos();
    });
  }

  refreshTodos() {
    this.loadingTodos = true;
    this.errorMsg = null;

    if (this.activeTab() === 'global') {
      this.todoService.listGlobal().subscribe({
        next: (d) => {
          this.globalTodos.set(d);
          this.loadingTodos = false;
        },
        error: () => {
          this.errorMsg = 'Failed to load global todos.';
          this.loadingTodos = false;
        },
      });
    } else {
      const wallet = this.walletConnection()?.actor;
      if (!wallet) {
        this.personalTodos.set([]);
        this.loadingTodos = false;
        return;
      }

      this.todoService.listPersonal(wallet).subscribe({
        next: (d) => {
          this.personalTodos.set(d);
          this.loadingTodos = false;
        },
        error: () => {
          this.errorMsg = 'Failed to load personal todos.';
          this.loadingTodos = false;
        },
      });
    }
  }

  addTodo() {
    const title = this.newTitle.trim();
    if (!title) return;

    const isGlobal = this.activeTab() === 'global';
    const wallet = this.walletConnection()?.actor;
    
    if (!wallet) {
      this.errorMsg = 'Please connect your wallet first.';
      return;
    }

    this.todoService.create(title, wallet, isGlobal).subscribe({
      next: () => {
        this.newTitle = '';
        this.refreshTodos();
      },
      error: () => {
        this.errorMsg = 'Failed to create todo.';
      },
    });
  }

  canToggleTodo(todo: Todo): boolean {
    const wallet = this.walletConnection()?.actor;
    if (!wallet) return false;
    
    // Global todos can be toggled by anyone with a wallet
    if (todo.isGlobal) return true;
    
    // Personal todos can only be toggled by the creator
    return todo.creator === wallet;
  }

  toggleTodo(todo: Todo) {
    if (!this.canToggleTodo(todo)) return;

    const wallet = this.walletConnection()?.actor;
    this.todoService.toggle(todo.id, wallet).subscribe({
      next: () => this.refreshTodos(),
      error: () => {
        this.errorMsg = 'Failed to toggle todo.';
      },
    });
  }

  canDeleteTodo(todo: Todo): boolean {
    const wallet = this.walletConnection()?.actor;
    if (!wallet) return false;
    
    // Only the creator can delete any todo
    return todo.creator === wallet;
  }

  deleteTodo(todo: Todo) {
    if (!this.canDeleteTodo(todo)) return;

    const wallet = this.walletConnection()?.actor;
    if (!wallet) return;

    this.todoService.delete(todo.id, wallet).subscribe({
      next: () => this.refreshTodos(),
      error: () => {
        this.errorMsg = 'Failed to delete todo.';
      },
    });
  }
}
